import { useEffect } from "react";
import { X, CheckCircle, AlertCircle, Info } from "lucide-react";

type ToastType = "success" | "error" | "info";

type ToastProps = {
  message: string;
  type: ToastType;
  onClose: () => void;
  duration?: number;
};

export default function Toast({
  message,
  type,
  onClose,
  duration = 3000,
}: ToastProps) {
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose();
    }, duration);

    return () => clearTimeout(timer);
  }, [duration, onClose]);

  const styles = {
    success: {
      bg: "bg-green-50 border-green-200",
      text: "text-green-800",
      icon: <CheckCircle className="h-5 w-5 text-green-600" />,
    },
    error: {
      bg: "bg-red-50 border-red-200",
      text: "text-red-800",
      icon: <AlertCircle className="h-5 w-5 text-red-600" />,
    },
    info: {
      bg: "bg-blue-50 border-blue-200",
      text: "text-blue-800",
      icon: <Info className="h-5 w-5 text-blue-600" />,
    },
  };

  const style = styles[type];

  return (
    <div
      data-toast-type={type}
      className={`flex min-w-0 max-w-full items-center gap-3 rounded-lg border-2 ${style.bg} p-4 shadow-lg animate-slide-in-right`}
    >
      {style.icon}
      <p className={`min-w-0 flex-1 text-sm font-medium [overflow-wrap:anywhere] ${style.text}`}>{message}</p>
      <button
        onClick={onClose}
        className={`${style.text} hover:opacity-70 transition-opacity`}
        aria-label="Dismiss"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
